import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useArees, useDepartaments } from '@/api/planificacio';
import { Desplegable, type OpcioDesplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { PERIODES, type Periode } from './suport';
import '@/pages/operacio/operacio.css';
import './informes.css';

export function SelectorPeriode({
  valor,
  onCanvia,
}: {
  valor: Periode;
  onCanvia: (p: Periode) => void;
}) {
  const { t } = useTranslation('informes');
  return (
    <div className="segmented" role="group" aria-label={t('comu.periode')}>
      {PERIODES.map((p) => (
        <button key={p} type="button" aria-pressed={valor === p} onClick={() => onCanvia(p)}>
          {t(`periodes.${p}`)}
        </button>
      ))}
    </div>
  );
}

/** Desplegable amb aspecte de xip i icona (filtres dels mockups). Blau quan té valor. */
export function XipSelect({
  icona,
  etiqueta,
  valor,
  onCanvia,
  opcions,
}: {
  icona: NomIcona;
  etiqueta: string;
  valor: string | number;
  onCanvia: (v: string) => void;
  /** Opcions del filtre; la de «Tots» amb valor ''. */
  opcions: OpcioDesplegable<string>[];
}) {
  const actiu = valor !== '';
  return (
    <span className={`xip-select${actiu ? ' xip-select--actiu' : ''}`}>
      <Icona nom={icona} />
      <Desplegable
        variant="chip"
        aria-label={etiqueta}
        valor={String(valor)}
        opcions={opcions}
        onCanvia={onCanvia}
      />
    </span>
  );
}

export function SelectorAmbit({
  departamentId,
  areaId,
  onCanvia,
}: {
  departamentId: number | '';
  areaId: number | '';
  onCanvia: (v: { departamentId: number | ''; areaId: number | '' }) => void;
}) {
  const { t } = useTranslation('informes');
  const departaments = useDepartaments();
  const arees = useArees();
  const visibles = (arees.data ?? []).filter(
    (a) => departamentId === '' || a.departamentId === departamentId,
  );
  return (
    <>
      <XipSelect
        icona="building"
        etiqueta={t('comu.departament')}
        valor={departamentId}
        onCanvia={(v) => onCanvia({ departamentId: v ? Number(v) : '', areaId: '' })}
        opcions={[
          { valor: '', etiqueta: t('comu.totsDepartaments') },
          ...(departaments.data ?? []).map((d) => ({ valor: String(d.id), etiqueta: d.nom })),
        ]}
      />
      <XipSelect
        icona="megaphone"
        etiqueta={t('comu.area')}
        valor={areaId}
        onCanvia={(v) => onCanvia({ departamentId, areaId: v ? Number(v) : '' })}
        opcions={[
          { valor: '', etiqueta: t('comu.totesArees') },
          ...visibles.map((a) => ({
            valor: String(a.id),
            etiqueta: a.nom,
            grup:
              departamentId === ''
                ? (departaments.data ?? []).find((d) => d.id === a.departamentId)?.nom
                : undefined,
          })),
        ]}
      />
    </>
  );
}

/** Variació percentual amb fletxa: vermell si puja (més activacions), verd si baixa. */
export function Tendencia({ valor }: { valor: number | null }) {
  const { t } = useTranslation('informes');
  if (valor == null) return <span className="c-gray">—</span>;
  const classe = valor > 0 ? 'c-red' : valor < 0 ? 'c-green' : 'c-gray';
  return (
    <span className={`tendencia ${classe}`}>
      {valor !== 0 ? <Icona nom={valor > 0 ? 'arrow-up' : 'arrow-down'} /> : null}
      {t('comu.tendencia', { signe: valor > 0 ? '+' : '', n: valor })}
    </span>
  );
}

export interface SegmentDonut {
  clau: string;
  nom: string;
  total: number;
  color: string;
}

/** Donut amb gradient cònic i total al centre (mockup W-40 «Distribució per tipus»). */
export function Donut({ segments, centre }: { segments: SegmentDonut[]; centre: ReactNode }) {
  const total = segments.reduce((s, x) => s + x.total, 0);
  let acumulat = 0;
  const trams = segments.map((x) => {
    const inici = total > 0 ? (acumulat / total) * 100 : 0;
    acumulat += x.total;
    const fi = total > 0 ? (acumulat / total) * 100 : 0;
    return `${x.color} ${inici}% ${fi}%`;
  });
  return (
    <div
      className="donut"
      style={{
        background: total > 0 ? `conic-gradient(${trams.join(', ')})` : 'var(--fill-2)',
      }}
    >
      <span>{centre}</span>
    </div>
  );
}

export interface BarraSerie {
  clau: string;
  etiqueta: string;
  valor: number;
  alt?: boolean;
  titol?: string;
}

/** Gràfic de barres senzill del sistema de disseny (`.bars`). */
export function Barres({ dades, etiqueta }: { dades: BarraSerie[]; etiqueta: string }) {
  const max = Math.max(1, ...dades.map((d) => d.valor));
  return (
    <div role="img" aria-label={etiqueta}>
      <div className="bars">
        {dades.map((d) => (
          <div className="bars__col" key={d.clau} title={d.titol}>
            <span
              className={`bars__seg${d.alt ? ' bars__seg--alt' : ''}`}
              style={{ height: `${Math.max(d.valor > 0 ? 3 : 0, (d.valor / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="bars__labels">
        {dades.map((d) => (
          <span key={d.clau} className="truncate">
            {d.etiqueta}
          </span>
        ))}
      </div>
    </div>
  );
}
